import {defineStore} from "pinia";
import {reactive, ref, watch} from "vue";
import {
  cleanObject,
  serializeFormData,
  snakeCaseDifference,
  snakeToCamel
} from "../helpers/CaseTransformer.js";
import {computeCandidateTrustScore} from "../helpers/TrustScore.js";
import CandidateApi from "../api/CandidateApi";
import {isEqual, merge} from "lodash";
import {useAccountStore} from "./AccountStore.js";

export const useCandidateProfileStore = defineStore("candidateProfile", () => {
  const state = reactive({
    trustScore: 53,
    avatar: "",
    banner: "",
    bannerFile: {
      path: "",
      file: null,
    },
    profilePhoto: {
      path: "",
      file: null,
    },
    about: "",
    summary: {
      prompt: "",
      answer: "",
    },
    location: "",
    lat: null,
    lng: null,
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    willingToRelocate: "",
    remote: "",
    interestedIn: [],
    industries: [],
    fullTime: [],
    availability: [],
    desiredHourlyPay: 15,
    compensationFlexibility: 0.10,
    prefersHourlyOverSalary: true,
    exceptionalAbility: [],
    profileVideo1: {
      path: "",
      prompt: "",
      file: null,
    },
    profileVideo2: {
      path: "",
      prompt: "",
      file: null,
    },
    skills: [],
    accomplishments: [],
    careerPassport: [],
    noPreviousWorkExperience: false,
    education: [],
    certificatesCertificationsAndLicenses: [],
    languages: [],
    studentAthlete: false,
    hobbies: "",
    military: false,
    linkedIn: "",
    instagram: "",
    facebook: "",
    website: "",
    hasResume: false,
  });
  const defaultState = cleanObject(state);
  const account = useAccountStore();

  watch(() => state.careerPassport?.length, (length) => {
    if (length) state.noPreviousWorkExperience = false;
  }, {flush: "sync"});

  watch(() => account.user?.id, (id, previousId) => {
    if (previousId && id !== previousId) $reset();
  }, {flush: "sync"});

  const isDirty = () => {
    return !isEqual(cleanObject(state), cleanObject(originalState));
  };

  const isInQuickFillOutMode = ref(false);
  const quickProfileUnrewardedPoints = ref(0);

  let originalState = {};
  let profileRequest = 0;
  const response = reactive({
    errors: {},
    isLoading: false,
  });

  const show = async (callback = undefined) => {
    const accountStore = useAccountStore();

    if (!accountStore.user || !accountStore.user.candidateId) await accountStore.fetchUserAccount();

    if (!accountStore.user || !accountStore.user.candidateId) return;
    const candidateId = accountStore.user.candidateId;
    const request = ++profileRequest;
    const isCurrent = () => request === profileRequest && candidateId === accountStore.user?.candidateId;

    response.isLoading = true;

    await new CandidateApi()
      .show(candidateId)
      .then((response) => {
        if (!isCurrent()) return;
        Object.keys(state).forEach((key) => delete state[key]);
        Object.assign(state, cleanObject(defaultState));
        for (const [key, value] of Object.entries(response.data)) {
          if (value === null) continue;
          const camelKey = snakeToCamel(key);
          const defaultValue = state[camelKey];

          if (defaultValue === Object(defaultValue) && !Array.isArray(defaultValue)) {
            state[camelKey] = merge({}, state[camelKey], value);
            continue;
          }

          state[camelKey] = value;
        }

        state["firstName"] = accountStore.user.firstName;
        state["lastName"] = accountStore.user.lastName;
        state["avatar"] = accountStore.user.avatar;
        state["profilePhoto"]["path"] = accountStore.user.avatar;
        state["bannerFile"]["path"] = state.banner;
      })
      .finally(() => {
        if (!isCurrent()) return;
        response.isLoading = false;
        originalState = cleanObject(state);
        setTrustScore(callback);
      });
  };

  const update = async (callback = undefined, showToast = true, shouldSetTrustScore = true) => {
    const accountStore = useAccountStore();

    if (!accountStore.user || !accountStore.user.candidateId) await accountStore.fetchUserAccount();

    if (!accountStore.user?.candidateId) return false;
    const candidateId = accountStore.user.candidateId;
    if (state.careerPassport?.length) state.noPreviousWorkExperience = false;

    if (showToast) window.progressBar.start();

    let dirtyFields = snakeCaseDifference(cleanObject(state), cleanObject(originalState));
    let formData = serializeFormData(dirtyFields);

    // Files must be handled separately
    if (state.profilePhoto?.file && state.profilePhoto?.file instanceof File) formData.append("profile_photo_file", state.profilePhoto.file);
    else if (state.profilePhoto?.delete === true) formData.append("delete_profile_photo", "1");
    if (state.profileVideo1?.file && state.profileVideo1?.file instanceof File) formData.append("profile_video1_file", state.profileVideo1.file);
    else if (state.profileVideo1?.delete === true) formData.append("delete_profile_video1", "1");
    if (state.profileVideo2?.file && state.profileVideo2?.file instanceof File) formData.append("profile_video2_file", state.profileVideo2.file);
    else if (state.profileVideo2?.delete === true) formData.append("delete_profile_video2", "1");
    if (state.bannerFile?.file && state.bannerFile?.file instanceof File) formData.append("banner_file", state.bannerFile.file);
    else if (state.bannerFile?.delete === true) formData.append("delete_banner", "1");

    state.certificatesCertificationsAndLicenses.forEach((certificate, index) => {
      if (certificate?.file && certificate.file instanceof File) formData.append(`certificates_certifications_and_licenses_file_${index}`, certificate.file);
    });

    let success = false;

    try {
      await new CandidateApi().updateWithFiles(candidateId, formData);
      if (candidateId !== accountStore.user?.candidateId) return false;

      if (state.profilePhoto.file) await accountStore.fetchUserAccount();

      // Unset files so they don't get sent again
      state.profilePhoto.file = null;
      state.profileVideo1.file = null;
      state.profileVideo2.file = null;
      state.bannerFile.file = null;

      // All the deleted fields should be false now
      state.profilePhoto.delete = false;
      state.profileVideo1.delete = false;
      state.profileVideo2.delete = false;
      state.bannerFile.delete = false;

      originalState = {};

      Object.assign(originalState, cleanObject(state));
      if (showToast) toast.success("Successfully saved your profile!");
      if (shouldSetTrustScore) setTrustScore(callback);
      success = true;
    } catch (error) {
      console.error(error);
    } finally {
      if (showToast) window.progressBar.complete();
    }

    return success;
  };

  const setTrustScore = (callback = undefined) => {
    let scores = computeCandidateTrustScore(state);
    if (callback) callback(scores);
    state.trustScore = scores.overall;
    originalState.trustScore = scores.overall;
  };

  const $reset = () => {
    profileRequest++;
    Object.keys(state).forEach((key) => delete state[key]);
    Object.assign(state, cleanObject(defaultState));
    originalState = {};
    isInQuickFillOutMode.value = false;
    quickProfileUnrewardedPoints.value = 0;
    response.errors = {};
    response.isLoading = false;
  };

  return {
    state,
    response,
    isInQuickFillOutMode,
    quickProfileUnrewardedPoints,
    show,
    update,
    isDirty,
    setTrustScore,
    $reset
  };
});
